/**
 *  @class
 *  @function SlideShow
 */
if (!customElements.get('slide-show')) {
  class SlideShow extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      if (this._flkty) return;
      // Wait for vendor Flickity (defer order / race on homepage).
      if (typeof window.Flickity !== 'function') {
        this._flickityRetries = (this._flickityRetries || 0) + 1;
        if (this._flickityRetries <= 50) {
          clearTimeout(this._flickityRetryTimer);
          this._flickityRetryTimer = setTimeout(() => this.connectedCallback(), 50);
        }
        return;
      }
      const slideshow = this,
        dataset = slideshow.dataset;
      
      const designMode = Boolean(window.Shopify && window.Shopify.designMode);

      const slideshow_slides = Array.from(slideshow.querySelectorAll('.carousel__slide'));

      // Single early-return guard
      if (slideshow_slides.length < 1) return;
      // If Flickity isn't available (e.g. assets not loaded), avoid breaking the page.
      const FlickityCtor = window.Flickity;
      if (typeof FlickityCtor !== 'function') return;
      const fizzy = window.fizzyUIUtils;

      const dots_enabled = dataset.dots === 'true',
        autoplay = designMode ? false : (() => {
          const raw = dataset.autoplay || '';
          if (raw === '' || raw === 'false') return false;
          const value = parseInt(raw, 10);
          return (Number.isNaN(value) || value <= 0) ? false : value;
        })(),
        align = dataset.align == 'center' ? 'center' : 'left',
        fade = dataset.fade == 'true',
        prev_button = slideshow.querySelector('.flickity-prev'),
        next_button = slideshow.querySelector('.flickity-next'),
        // In some DOM states the dots/progress can end up as siblings of <slide-show>
        custom_dots = slideshow.querySelector('.flickity-page-dots') || slideshow.parentNode?.querySelector('.flickity-page-dots'),
        progress_bar = slideshow.parentNode?.querySelector('.flickity-progress--bar'),
        animations = [],
        rightToLeft = document.dir === 'rtl',
        animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';

      // Cache classList checks — avoids repeated DOM reads throughout constructor
      const isImageTextImage = slideshow.classList.contains('image-with-text-slideshow__image');
      const isImageTextContent = slideshow.classList.contains('image-with-text-slideshow__content');
      const isTestimonials = slideshow.classList.contains('testimonials__carousel');
      const isCustomerReviewsContent = slideshow.classList.contains('customer-reviews__content');
      const isCustomerReviewsImage = slideshow.classList.contains('customer-reviews__image');
      const isCustomDots = slideshow.classList.contains('custom-dots');
      const isMainSlideshow = slideshow.classList.contains('main-slideshow');
      const isShoppableReels = slideshow.classList.contains('shoppable-video-reels--carousel');
      const isProducts = slideshow.classList.contains('products');
      const isCollectionGrid = slideshow.classList.contains('collection-grid__carousel');

      slideshow._previousIndex = 0;

      const args = {
        wrapAround: true,
        cellAlign: align,
        pageDots: isCustomDots ? false : dots_enabled,
        contain: true,
        fade,
        autoPlay: autoplay,
        rightToLeft,
        prevNextButtons: false,
        cellSelector: '.carousel__slide',
        on: {}
      };

      if (isImageTextImage) {
        const image_slideshow_slides = slideshow.querySelectorAll('.image-with-text-slideshow__image-media');
        args.draggable = false;
        args.asNavFor = slideshow.parentNode.querySelector('.image-with-text-slideshow__content');
        if (image_slideshow_slides.length && image_slideshow_slides[0].classList.contains('desktop-height-auto')) {
          args.adaptiveHeight = true;
        }
      }
      if (isCustomerReviewsImage) {
        args.draggable = false;
        args.asNavFor = slideshow.parentNode.querySelector('.customer-reviews__content');
      }
      if (isImageTextContent || isTestimonials || isCustomerReviewsContent || isCustomerReviewsImage) {
        args.adaptiveHeight = true;
      }

      // Cache dot elements once — prevents querySelectorAll on every slide change
      const cached_dot_list = custom_dots ? Array.from(custom_dots.querySelectorAll('li')) : null;

      if (isCustomDots) {
        if (animations_enabled && isMainSlideshow) {
          this.prepareAnimations(slideshow, animations);
        }
        args.pauseAutoPlayOnHover = false;

        args.on = {
          staticClick: function () {
            this.unpausePlayer();
          },
          ready: function () {
            let flkty = this;

            if (animations_enabled && isMainSlideshow) {
              slideshow.animateSlides(0, animations, true);
              gsap.set(slideshow.querySelectorAll('.subheading,.split-text,.button'), { visibility: 'visible' });
            }

            if (dots_enabled && cached_dot_list) {
              cached_dot_list.forEach((dot, i) => {
                dot.addEventListener('click', () => flkty.select(i));
              });
              if (this.selectedIndex < cached_dot_list.length) {
                cached_dot_list[this.selectedIndex]?.classList.add('is-selected');
              }
            }

            // Start autoplay for custom dots after ready
            if (autoplay && typeof flkty.playPlayer === 'function') {
              flkty.playPlayer();
            }

            document.fonts.ready.then(() => requestAnimationFrame(() => flkty.resize()));

            // Video Support — shoppable reels stay paused until user presses play.
            if (!isShoppableReels) {
              const video_container = flkty.selectedElement
                ? flkty.selectedElement.querySelector('.slideshow__slide-video-bg')
                : flkty.cells[0].element.querySelector('.slideshow__slide-video-bg');
              if (video_container) {
                const iframe = video_container.querySelector('iframe');
                const video = video_container.querySelector('video');
                if (iframe) {
                  iframe.onload = () => slideshow.videoPlay(video_container);
                } else if (video) {
                  slideshow.videoPlay(video_container);
                }
              }
            } else {
              flkty.cells.forEach((cell) => {
                const vc = cell.element.querySelector('.slideshow__slide-video-bg');
                if (!vc) return;
                const v = vc.querySelector('video');
                if (v) {
                  v.dataset.userPaused = 'true';
                  v.pause();
                }
                const playToggle = vc.querySelector('.video-controls--play-pause');
                if (playToggle) playToggle.classList.add('paused');
              });
            }
          },
          change: function (index) {
            // IMPORTANT: use `this` (Flickity instance). Outer `flkty` is still in TDZ
            // while Flickity's constructor runs and fires change/ready synchronously.
            const flktyInstance = this;
            const firstCell = flktyInstance.cells[0].element;
            if (firstCell.classList.contains('is-initial-selected')) {
              firstCell.classList.remove('is-initial-selected');
            }
            const previousIndex = fizzy && typeof fizzy.modulo === 'function' ? fizzy.modulo(flktyInstance.selectedIndex - 1, flktyInstance.slides.length) : flktyInstance.selectedIndex,
              nextIndex = fizzy && typeof fizzy.modulo === 'function' ? fizzy.modulo(flktyInstance.selectedIndex + 1, flktyInstance.slides.length) : flktyInstance.selectedIndex;

            // Direction detection.
            const prevIdx = slideshow._previousIndex;
            const total = flktyInstance.slides.length;
            const diff = index - prevIdx;
            const isForward = diff > 0 ? (diff <= total / 2) : (Math.abs(diff) > total / 2);
            slideshow._previousIndex = index;

            // Animations.
            if (animations_enabled && isMainSlideshow) {
              slideshow.animateSlides(index, animations, isForward, previousIndex);
            }

            // Color changes — deferred to avoid forced reflow during change callback
            const selectedElement = flktyInstance.selectedElement;
            requestAnimationFrame(() => {
              const cs = getComputedStyle(selectedElement);
              slideshow.style.setProperty('--color-body', cs.getPropertyValue('--color-body'));
              slideshow.style.setProperty('--color-body-rgb', cs.getPropertyValue('--color-body-rgb'));
            });

            // Custom Dots.
            if (dots_enabled && cached_dot_list) {
              cached_dot_list.forEach((dot) => dot.classList.remove('is-selected'));
              cached_dot_list[flktyInstance.selectedIndex].classList.add('is-selected');
            }

            // AutoPlay — stop immediately, restart after animation completes
            if (autoplay) {
              flktyInstance.stopPlayer();
              if (!(animations_enabled && isMainSlideshow && slideshow.dataset.transition == 'swipe')) {
                flktyInstance.playPlayer();
              }
            }

            // Video Support.
            // Pause neighbors (and for shoppable: pause all non-selected).
            if (isShoppableReels) {
              flktyInstance.cells.forEach((cell, i) => {
                if (i === index) return;
                const vc = cell.element.querySelector('.slideshow__slide-video-bg');
                if (vc) slideshow.videoPause(vc, true);
              });
              // Selected stays paused until user play — keep poster/play UI.
              const selectedVc = flktyInstance.cells[index].element.querySelector('.slideshow__slide-video-bg');
              if (selectedVc) {
                const hostedVideo = selectedVc.querySelector('video');
                if (hostedVideo) {
                  hostedVideo.dataset.userPaused = 'true';
                  hostedVideo.pause();
                }
                const playToggle = selectedVc.querySelector('.video-controls--play-pause');
                if (playToggle) playToggle.classList.add('paused');
              }
            } else {
              // previous slide
              const video_container_prev = flktyInstance.cells[previousIndex].element.querySelector('.slideshow__slide-video-bg');
              if (video_container_prev) {
                slideshow.videoPause(video_container_prev);
              }
              // next slide
              const video_container_next = flktyInstance.cells[nextIndex].element.querySelector('.slideshow__slide-video-bg');
              if (video_container_next) {
                slideshow.videoPause(video_container_next);
              }
              // current slide
              const video_container = flktyInstance.cells[index].element.querySelector('.slideshow__slide-video-bg');
              if (video_container) {
                const hostedVideo = video_container.querySelector('video');
                if (hostedVideo) hostedVideo.dataset.userPaused = 'false';
                const iframe = video_container.querySelector('iframe');
                if (iframe) {
                  if (iframe.classList.contains('lazyload')) {
                    iframe.addEventListener('lazybeforeunveil', () => slideshow.videoPlay(video_container));
                    if (typeof lazySizes !== 'undefined' && lazySizes.loader && typeof lazySizes.loader.checkElems === 'function') {
                      lazySizes.loader.checkElems();
                    }
                  } else {
                    slideshow.videoPlay(video_container);
                  }
                } else if (hostedVideo) {
                  slideshow.videoPlay(video_container);
                }
              }
            }
          }
        };
      }
      if (isShoppableReels) {
        // Avoid accidental slide change from tiny pointer movement on controls.
        args.dragThreshold = 12;
        args.on.staticClick = function (event, pointer, cellElement, cellIndex) {
          const target = event && event.target;
          if (target && target.closest && target.closest('button, a, video-controls, .shoppable-video-reels--button, .shoppable-video-reels--product')) {
            return;
          }
          if (typeof cellIndex == 'number') {
            this.select(cellIndex);
          }
        };
      }
      if (isMainSlideshow) {
        if (slideshow.classList.contains('desktop-height-image') || slideshow.classList.contains('mobile-height-image')) {
          args.adaptiveHeight = true;
        }
      }
      if (isProducts || isCollectionGrid) {
        args.wrapAround = false;
        args.on.ready = function () {
          if (next_button && isProducts) {
            let resizeRaf = 0;
            slideshow._resizeHandler = function () {
              cancelAnimationFrame(resizeRaf);
              resizeRaf = requestAnimationFrame(() => {
                slideshow.centerArrows(slideshow._flkty, prev_button, next_button);
              });
            };
            window.addEventListener('resize', slideshow._resizeHandler);
          }
          window.dispatchEvent(new Event('resize'));
        };
      }
      if (progress_bar) {
        let scrollRaf = 0;
        args.wrapAround = false;
        args.on.scroll = function (progress) {
          cancelAnimationFrame(scrollRaf);
          scrollRaf = requestAnimationFrame(() => {
            progress = Math.max(0, Math.min(1, progress));
            progress_bar.style.width = progress * 100 + '%';
          });
        };
      }
      let flkty;
      try {
        flkty = new FlickityCtor(slideshow, args);
      } catch (err) {
        console.error('[slide-show] Flickity init failed', err);
        return;
      }

      // Start autoplay after Flickity is fully initialized
      if (isCustomDots && autoplay && args.on.ready) {
        // ready callback will handle autoplay start
      } else if (!isCustomDots && autoplay && typeof flkty.playPlayer === 'function') {
        // Non-custom dots: start immediately
        flkty.playPlayer();
      }

      // Defensive dots binding: some apps/themes move the dots outside <slide-show>.
      // Use event delegation at section level so dots work regardless of placement.
      if (dots_enabled && !slideshow.dataset.dotsDelegated) {
        slideshow.dataset.dotsDelegated = 'true';
        const scope = slideshow.closest('.section-slideshow') || slideshow.parentNode || document;
        scope.addEventListener('click', (e) => {
          const li = e.target && e.target.closest ? e.target.closest('.flickity-page-dots li') : null;
          if (!li) return;
          // Ensure dot clicks aren't swallowed by other handlers.
          if (typeof e.preventDefault === 'function') e.preventDefault();
          if (typeof e.stopPropagation === 'function') e.stopPropagation();
          if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
          // If multiple slideshows exist in scope, ensure this click belongs to this instance.
          const dotsRoot = li.closest('.flickity-page-dots');
          if (!dotsRoot) return;
          const nearestShow = dotsRoot.closest('slide-show') || dotsRoot.parentNode?.querySelector?.('slide-show');
          if (nearestShow && nearestShow !== slideshow) return;
          const items = Array.from(dotsRoot.querySelectorAll('li'));
          let idx = items.indexOf(li);
          // Fallback: aria-label is set to "1","2",...
          if (idx < 0 && li.getAttribute) {
            const aria = parseInt(li.getAttribute('aria-label') || '', 10);
            if (!Number.isNaN(aria)) idx = aria - 1;
          }
          if (idx >= 0) flkty.select(idx);
        }, true);
      }

      // Store references for cleanup in disconnectedCallback
      this._flkty = flkty;
      this._animations = animations;
      this._animations_enabled = animations_enabled && isMainSlideshow;

      dataset.initiated = true;

      if (prev_button) {
        prev_button.addEventListener('click', () => flkty.previous());
        prev_button.addEventListener('keyup', () => flkty.previous());
        next_button.addEventListener('click', () => flkty.next());
        next_button.addEventListener('keyup', () => flkty.next());
      }

      if (designMode) {
        slideshow.addEventListener('shopify:block:select', (event) => {
          const index = slideshow_slides.indexOf(event.target);
          flkty.select(index);
        });
      }
    }
    disconnectedCallback() {
      clearTimeout(this._flickityRetryTimer);
      // Destroy Flickity instance
      if (this._flkty) {
        this._flkty.destroy();
        this._flkty = null;
      }

      // Kill all GSAP animation timelines
      if (this._animations_enabled && this._animations) {
        this._animations.forEach((tl) => tl.kill());
        this._animations = null;
      }

      // Remove resize listener
      if (this._resizeHandler) {
        window.removeEventListener('resize', this._resizeHandler);
        this._resizeHandler = null;
      }

      // Reset animation state so prepareAnimations can re-run
      delete this.dataset.animationsReady;
      this.dataset.initiated = false;
    }
    videoPause(video_container, markUserPaused) {
      setTimeout(() => {
        if (!video_container) return;
        if (video_container.dataset.provider === 'hosted') {
          const video = video_container.querySelector('video');
          if (!video) return;
          if (markUserPaused) video.dataset.userPaused = 'true';
          video.pause();
          const playToggle = video_container.querySelector('.video-controls--play-pause');
          if (playToggle) playToggle.classList.add('paused');
        } else {
          const iframe = video_container.querySelector('iframe');
          if (!iframe || !iframe.contentWindow) return;
          const provider = video_container.dataset.provider;
          const message = provider === 'youtube' ? { event: "command", func: "pauseVideo", args: "" } : { method: "pause" };
          iframe.contentWindow.postMessage(JSON.stringify(message), "*");
        }
      }, 10);
    }
    videoPlay(video_container) {
      setTimeout(() => {
        if (!video_container) return;
        if (video_container.dataset.provider === 'hosted') {
          const video = video_container.querySelector('video');
          if (!video) return;
          // Respect intentional pause from controls / video tap.
          if (video.dataset.userPaused === 'true') return;
          video.muted = true;
          const playPromise = video.play();
          const playToggle = video_container.querySelector('.video-controls--play-pause');
          if (playPromise && typeof playPromise.then === 'function') {
            playPromise.then(() => {
              if (playToggle) playToggle.classList.remove('paused');
            }).catch(() => {
              if (playToggle) playToggle.classList.add('paused');
            });
          } else if (playToggle) {
            playToggle.classList.toggle('paused', video.paused);
          }
        } else {
          const iframe = video_container.querySelector('iframe');
          if (!iframe || !iframe.contentWindow) return;
          const provider = video_container.dataset.provider;
          const message = provider === 'youtube' ? { event: "command", func: "playVideo", args: "" } : { method: "play" };
          iframe.contentWindow.postMessage(JSON.stringify(message), "*");
        }
      }, 10);
    }
    prepareAnimations(slideshow, animations) {
      if (!slideshow.dataset.animationsReady) {
        const split_text_els = slideshow.querySelectorAll('.split-text');
        if (typeof SplitText !== 'undefined') {
          new SplitText(split_text_els, { type: 'lines, words', linesClass: 'line-child' });
        } else {
          // Animations are optional; if SplitText isn't present, skip timeline prep.
          slideshow.dataset.animationsReady = true;
          return;
        }

        var s = 1 + (3 - (parseInt(slideshow.dataset.animationSpeed, 10) || 3)) * 0.3;

        slideshow.querySelectorAll('.slideshow__slide').forEach((item, i) => {
          // Cache all element refs per slide — avoids repeated querySelector in timeline definitions
          const bg = item.querySelector('.slideshow__slide-bg');
          const subheading = item.querySelector('.subheading');
          const headingLines = item.querySelectorAll('.slideshow__slide-content--heading .line-child div');
          const bodyLines = item.querySelectorAll('p:not(.subheading) .line-child div');
          const buttons = item.querySelectorAll('.button');

          const tl = gsap.timeline({ paused: true });
          let button_offset = 0;

          animations[i] = tl;

          tl.to(bg, { duration: 1.5 * s, scale: 1, ease: 'none' }, "start");

          if (subheading) {
            tl.to(subheading, { duration: 0.8 * s, autoAlpha: 1 }, 0);
            button_offset += 0.8 * s;
          }
          if (headingLines.length) {
            const h1_duration = 0.8 * s + ((headingLines.length - 1) * 0.08 * s);
            tl.from(headingLines, { duration: h1_duration, yPercent: '100', rotationZ: 10, stagger: 0.08 * s }, 0);
            button_offset += h1_duration;
          }
          if (bodyLines.length) {
            const p_duration = 0.8 * s + ((bodyLines.length - 1) * 0.04 * s);
            tl.from(bodyLines, { duration: p_duration, yPercent: '100', rotationZ: 10, stagger: 0.04 * s }, 0);
            button_offset += p_duration;
          }
          if (buttons.length) {
            tl.fromTo(buttons, { y: '100%' }, { duration: 0.8 * s, y: '0%', stagger: 0.15 * s }, button_offset * 0.2);
          }
          item.dataset.timeline = tl;
        });
        slideshow.dataset.animationsReady = true;
      }
    }
    animateSlides(i, animations, isForward, prevSlideIndex) {
      var slides = this.querySelectorAll('.slideshow__slide');
      var slide = slides[i];
      var prevSlide = prevSlideIndex !== undefined ? slides[prevSlideIndex] : null;
      var s = 1 + (3 - (parseInt(this.dataset.animationSpeed, 10) || 3)) * 0.3;
      if (this.dataset.transition == 'swipe') {
        if (this._lastPrevSlide) {
          gsap.killTweensOf(this._lastPrevSlide, 'clipPath');
          gsap.set(this._lastPrevSlide, { clearProps: 'clipPath' });
        }
        this._lastPrevSlide = prevSlide;
        if (slide) {
          var self = this;
          var from = isForward ? 'polygon(100% 0%, 100% 0%, 100% 100%, 100% 100%)' : 'polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)';
          gsap.fromTo(slide, { clipPath: from }, {
            duration: 0.85 * s,
            clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)',
            ease: 'power3.inOut',
            overwrite: true,
            onComplete: function() {
              if (self._flkty && self._flkty.options.autoPlay) {
                self._flkty.playPlayer();
              }
            }
          });
        }
      }
      animations[i].timeScale(1).restart();
      if (prevSlideIndex !== undefined) {
        animations[prevSlideIndex].timeScale(3).reverse();
      }
    }
    centerArrows(flickity, prev_button, next_button) {
      const first_cell = flickity.cells[0],
        image_height = first_cell.element.querySelector('.product-card--featured-image')?.clientHeight || 0;
      let max_height = 0;

      flickity.cells.forEach((item) => {
        if (item.size.height > max_height) {
          max_height = item.size.height;
        }
      });

      if (max_height > image_height) {
        const difference = (max_height - image_height) / -2;
        prev_button.style.transform = 'translateY(' + difference + 'px)';
        next_button.style.transform = 'translateY(' + difference + 'px)';
      }
    }
  }
  customElements.define('slide-show', SlideShow);
}
